import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, typography } from '../../core/theme/tokens';

export type MilestoneLegendItem = {
  x: number;
  prevX: number;
  barWidth: number;
  color: string;
  yearsLabel: string;
  amountLabel: string;
};

const LABEL_W = 48;
const MIN_LABEL_GAP = 46;

type Props = {
  width: number;
  items: MilestoneLegendItem[];
  remainToX?: number | null;
};

/**
 * Color bar + labels laid out on the same x-scale as the milestone line,
 * so a $200k marker and its "$200k / 1.7y" sit on the same year.
 */
export function WealthMilestoneLegend({ width, items, remainToX }: Props) {
  let lastLabelX = -Infinity;
  const labeled = items.map((item, index) => {
    const show =
      index === 0 ||
      index === items.length - 1 ||
      item.x - lastLabelX >= MIN_LABEL_GAP;
    if (show) lastLabelX = item.x;
    return { ...item, show };
  });

  return (
    <View style={[styles.track, { width }]}>
      {labeled.map((item, i) => (
        <View
          key={`bar-${i}`}
          style={[
            styles.bar,
            {
              left: item.prevX,
              width: Math.max(item.barWidth, 2),
              backgroundColor: item.color,
            },
          ]}
        />
      ))}
      {remainToX != null && remainToX > 0 && items.length > 0 && (
        <View
          style={[
            styles.bar,
            styles.remain,
            {
              left: items[items.length - 1]!.x,
              width: Math.max(remainToX - items[items.length - 1]!.x, 0),
            },
          ]}
        />
      )}
      {labeled.map((item, i) =>
        item.show ? (
          <View
            key={`lab-${i}`}
            style={[
              styles.labelCol,
              { left: Math.min(Math.max(item.x - LABEL_W / 2, 0), width - LABEL_W) },
            ]}
            pointerEvents="none"
          >
            <Text style={[styles.amount, { color: item.color }]} numberOfLines={1}>
              {item.amountLabel}
            </Text>
            <Text style={styles.years} numberOfLines={1}>
              {item.yearsLabel}y
            </Text>
          </View>
        ) : null,
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  track: {
    height: 54,
    position: 'relative',
  },
  bar: {
    position: 'absolute',
    top: 0,
    height: 12,
    borderRadius: 4,
  },
  remain: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  labelCol: {
    position: 'absolute',
    top: 16,
    width: LABEL_W,
    alignItems: 'center',
    gap: 1,
  },
  amount: {
    ...typography.caption,
    fontSize: 10,
    lineHeight: 13,
    includeFontPadding: false,
    fontWeight: '700',
    textAlign: 'center',
  },
  years: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    lineHeight: 13,
    includeFontPadding: false,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
    textAlign: 'center',
  },
});
